import React, {useContext, useEffect, useRef, useMemo, memo} from 'react';
import PropTypes from 'prop-types';
import DataRow from './DataRow';
import DataCell from './DataCell';
import './DataSet.scss';
import DataTableContext from './DataTableContext';
import CategoriesContext from '../CategoriesContext';
import {CLEAR_SEARCH, EMPTY_ELEMENT} from '../../utils/ftemConstants';

const TitleCell = memo(({title, index, titlesLength, dataSetTitle, colorType, isMulti}) => (
    <DataCell
        key={`${dataSetTitle}-${title.id}`}
        isTitle={true}
        colorType={colorType}
        position={isMulti ? (index === 0 ? 'top-left' : index === titlesLength - 1 ? 'top-right' : '') : 'top'}
        isRightEndCell={isMulti && index === titlesLength - 1}
    >
        {title.name}
    </DataCell>
), (prev, next) => (
    prev.title.id === next.title.id
    && prev.index === next.index
    && prev.titlesLength === next.titlesLength
    && prev.colorType === next.colorType
));

TitleCell.displayName = 'TitleCell';

const useDebouncedScroll = (scrollPosition, dataSetTableRef) => {
    const scrollStateRef = useRef({
        parentScroll: {top: 0, left: 0},
        isLeftScroll: false,
        isTopScroll: true,
        titlePositions: {},
    });
    const [, forceUpdate] = React.useReducer(x => x + 1, 0);
    const timeoutRef = useRef();

    const updateScroll = useMemo(() => {
        let scheduled;
        return () => {
            if (scheduled) return;
            scheduled = setTimeout(() => {
                if (!dataSetTableRef.current) return;

                const state = scrollStateRef.current;
                const rect = dataSetTableRef.current.getBoundingClientRect();
                let shouldUpdate = false;

                if (scrollPosition.top !== state.parentScroll.top) {
                    state.isLeftScroll = false;
                    state.isTopScroll = true;
                    state.titlePositions = {left: `${scrollPosition.left + 35}px`};
                    state.parentScroll.top = scrollPosition.top;
                    shouldUpdate = true;
                }
                if (scrollPosition.left !== state.parentScroll.left) {
                    state.isLeftScroll = true;
                    state.isTopScroll = false;
                    state.titlePositions = {top: `${rect.top - 40}px`};
                    state.parentScroll.left = scrollPosition.left;
                    shouldUpdate = true;
                }

                if (shouldUpdate) forceUpdate();
                scheduled = null;
            }, 50);
            timeoutRef.current = scheduled;
        };
    }, [scrollPosition.left, scrollPosition.top, dataSetTableRef]);

    useEffect(() => {
        updateScroll();
        return () => clearTimeout(timeoutRef.current);
    }, [updateScroll]);

    return scrollStateRef;
};

const DataSet = memo(({dataSetTitle, headersContent, haveToRerender, children, isSearch, isFixedCategory}) => {
    const dataSetTableRef = useRef(null);
    const {scrollPosition} = useContext(DataTableContext);
    const {search} = useContext(CategoriesContext);
    const scrollStateRef = useDebouncedScroll(scrollPosition, dataSetTableRef);

    const searchedTerm = search[0];
    const isFound = useMemo(() => {
        if (!searchedTerm?.trim() || searchedTerm === EMPTY_ELEMENT) return true;
        const flatSearch = isSearch.flat();
        if (!flatSearch.length) return false;

        const searchLower = searchedTerm.toLowerCase().trim();
        return flatSearch.some(elm => elm.text?.length && elm.text !== EMPTY_ELEMENT
            && elm.text.some(p => p.type === 'paragraph'
                && Array.isArray(p.children)
                && p.children.some(c => c.type === 'text'
                    && typeof c.text === 'string'
                    && c.text.toLowerCase().includes(searchLower))));
    }, [isSearch, searchedTerm]);

    const headers = useMemo(() => Object.entries(headersContent).map(([key, titlesSet]) => {
        const isMulti = titlesSet.length > 1;
        return titlesSet.map((title, index) => (
            <TitleCell
                title={title}
                index={index}
                titlesLength={titlesSet.length}
                dataSetTitle={dataSetTitle}
                colorType={key.toLowerCase()}
                isMulti={isMulti}
            />
        ));
    }), [headersContent, dataSetTitle]);

    useEffect(() => {
        if (!haveToRerender) return;
        const state = scrollStateRef.current;
        state.isLeftScroll = false;
        state.isTopScroll = true;
        state.titlePositions = {left: `${scrollPosition.left + 40}px`};
    }, [haveToRerender, scrollPosition.left, scrollStateRef]);

    if (!isFixedCategory) {
        if (!isFound && (!searchedTerm || searchedTerm !== CLEAR_SEARCH)) {
            return null;
        }
    }

    const state = scrollStateRef.current;

    return (
        <div className="sski-c-data-set p-2 is-position-relative">
            {state.isLeftScroll && (
                <p
                    style={state.titlePositions}
                    className="sski-c-data-set__title is-position-fixed is-size-5 has-text-weight-bold"
                >
                    {dataSetTitle}
                </p>
            )}
            {state.isTopScroll && (
                <p
                    style={state.titlePositions}
                    className="sski-c-data-set__title is-position-absolute is-size-5 has-text-weight-bold"
                >
                    {dataSetTitle}
                </p>
            )}
            <div ref={dataSetTableRef} className="sski-c-data-set__data-table">
                <DataRow>{headers}</DataRow>
                {children}
            </div>
        </div>
    );
}, (prev, next) => (
    prev.dataSetTitle === next.dataSetTitle
    && prev.haveToRerender === next.haveToRerender
    && prev.headersContent === next.headersContent
    && prev.isSearch === next.isSearch
    && prev.children === next.children
));

DataSet.displayName = 'DataSet';

DataSet.propTypes = {
    dataSetTitle: PropTypes.string.isRequired,
    headersContent: PropTypes.object.isRequired,
    haveToRerender: PropTypes.bool,
};

DataSet.defaultProps = {
    haveToRerender: false,
};

export default DataSet;
